import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { useAuth } from '../../App';
import { formatBytes } from '../../utils';

const GIB = 1024 ** 3;

// #192 D3, 2.c.1 — estado de la carga inicial en castellano, con el
// porcentaje y los bytes reales sólo cuando hay con qué calcularlos (nunca
// "0%"/"NaN" en el lugar de un dato que falta). Usa el `job` real (lo que ya
// persiste el backend), no el `form` en edición: un toggle recién tildado en
// un job nuevo todavía no tiene nada real que reportar.
function initialLoadStatusText(job) {
  const state = job?.initial_load_state;
  if (state === 'pending') return 'activada, esperando el primer tramo';
  if (state === 'in_progress') {
    const total = job?.initial_load_bytes_total;
    if (!total) return 'en curso';
    const done = job?.initial_load_bytes_done || 0;
    const pct = Math.round((done / total) * 100);
    return `en curso, ${pct}% (${formatBytes(done)} de ${formatBytes(total)})`;
  }
  if (state === 'done') return 'completada';
  return null;
}

// #192 D3, punto 6 (corrección) — el campo carga en GiB pero el panel y las
// notificaciones muestran GB (1 GiB ≈ 1.07 GB): sin esta ayuda, quien pone
// "1" ve avanzar "1.1 GB" y no entiende de dónde sale. Nunca cambia la
// unidad del campo ni lo que se guarda, sólo informa la equivalencia.
export function gibToGbHelpText(gibValue) {
  const n = parseFloat(gibValue);
  if (!gibValue || Number.isNaN(n) || n <= 0) return null;
  const gb = (n * GIB) / 1e9;
  return `${n} GiB ≈ ${gb.toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Schedule builder helpers
// ---------------------------------------------------------------------------

const FREQ_OPTIONS = [
  { value: 'hourly',  label: 'Cada hora'   },
  { value: 'daily',   label: 'Cada día'    },
  { value: 'weekly',  label: 'Cada semana' },
  { value: 'monthly', label: 'Cada mes'    },
];
const DOW_OPTIONS = [
  { value: '1', label: 'Lunes' }, { value: '2', label: 'Martes' },
  { value: '3', label: 'Miércoles' }, { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' }, { value: '6', label: 'Sábado' },
  { value: '0', label: 'Domingo' },
];

function buildCron(freq, hour, minute, dow, dom) {
  if (freq === 'hourly')  return `${minute} * * * *`;
  if (freq === 'daily')   return `${minute} ${hour} * * *`;
  if (freq === 'weekly')  return `${minute} ${hour} * * ${dow}`;
  if (freq === 'monthly') return `${minute} ${hour} ${dom} * *`;
  return `${minute} ${hour} * * *`;
}

function parseCron(cron) {
  if (!cron) return { freq: 'daily', hour: 2, minute: 0, dow: '1', dom: 1 };
  const p = cron.trim().split(/\s+/);
  if (p.length !== 5) return { freq: 'daily', hour: 2, minute: 0, dow: '1', dom: 1 };
  const [min, hr, dom, , dow] = p;
  const minute = parseInt(min) || 0;
  const hour   = parseInt(hr)  || 0;
  if (hr === '*')  return { freq: 'hourly',  hour, minute, dow: dow !== '*' ? dow : '1', dom: dom !== '*' ? parseInt(dom) : 1 };
  if (dom !== '*') return { freq: 'monthly', hour, minute, dow: '1', dom: parseInt(dom) };
  if (dow !== '*') return { freq: 'weekly',  hour, minute, dow, dom: 1 };
  return              { freq: 'daily',   hour, minute, dow: '1', dom: 1 };
}

export function ScheduleBuilder({ value, onChange }) {
  const parsed = parseCron(value);
  const [freq,   setFreq]   = useState(parsed.freq);
  const [hour,   setHour]   = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [dow,    setDow]    = useState(parsed.dow);
  const [dom,    setDom]    = useState(parsed.dom);

  useEffect(() => {
    onChange(buildCron(freq, hour, minute, dow, dom));
  }, [freq, hour, minute, dow, dom]);  // eslint-disable-line

  const selStyle = { background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', cursor: 'pointer', width: '100%' };
  const lblStyle = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' };

  const hours   = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 5, 10, 15, 20, 30, 45];
  const doms    = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: 14, marginBottom: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={lblStyle}>Frecuencia</label>
        <select value={freq} onChange={e => setFreq(e.target.value)} style={selStyle}>
          {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {freq !== 'hourly' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lblStyle}>Hora</label>
            <select value={hour} onChange={e => setHour(Number(e.target.value))} style={selStyle}>
              {hours.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
            </select>
          </div>
          <div>
            <label style={lblStyle}>Minuto</label>
            <select value={minute} onChange={e => setMinute(Number(e.target.value))} style={selStyle}>
              {minutes.map(m => <option key={m} value={m}>:{String(m).padStart(2,'0')}</option>)}
            </select>
          </div>
        </div>
      )}

      {freq === 'weekly' && (
        <div style={{ marginBottom: 12 }}>
          <label style={lblStyle}>Día de la semana</label>
          <select value={dow} onChange={e => setDow(e.target.value)} style={selStyle}>
            {DOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}

      {freq === 'monthly' && (
        <div style={{ marginBottom: 12 }}>
          <label style={lblStyle}>Día del mes</label>
          <select value={dom} onChange={e => setDom(Number(e.target.value))} style={selStyle}>
            {doms.map(d => <option key={d} value={d}>Día {d}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>CRON:</span>
        <span style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>
          {buildCron(freq, hour, minute, dow, dom)}
        </span>
        <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
          hora de Argentina (ART / UTC-3)
        </span>
      </div>
    </div>
  );
}

// Estilos reutilizables para el panel de credenciales DB
const dbLbl = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--mono)' };
const dbInp = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', boxSizing: 'border-box' };

// Exclusiones sugeridas al crear un job de archivos nuevo (#133): ~$* cubre los
// temporales de bloqueo de Office (Word/Excel/PowerPoint) que generaban WARN
// espurios en backups sobre recursos compartidos de Windows.
const DEFAULT_FILE_EXCLUDES = [
  '~$*',
  '*.tmp',
  'Thumbs.db',
  'desktop.ini',
  'pagefile.sys',
  'hiberfil.sys',
  '$Recycle.Bin',
  'System Volume Information',
];

export function JobModal({ job, servers, onClose, onSaved, readOnly = false }) {
  const isNew = !job?.id;

  const [defaultHour, setDefaultHour] = useState(2);
  useEffect(() => {
    if (isNew) {
      fetch('/api/settings/public-defaults')
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.default_backup_hour) {
            const h = parseInt(data.default_backup_hour.split(':')[0]) || 2;
            setDefaultHour(h);
            setForm(f => ({ ...f, schedule: buildCron('daily', h, 0, '1', 1) }));
          }
        })
        .catch(() => {});
    }
  }, [isNew]);

  const defaultCron = job?.schedule || buildCron('daily', defaultHour, 0, '1', 1);

  const [form, setForm] = useState({
    server_id:      job?.server_id || (servers[0]?.id || ''),
    name:           job?.name || '',
    job_type:       job?.job_type || 'files',
    schedule:       defaultCron,
    retention_days: job?.retention_days || 90,
    paths:          (job?.paths || []).join('\n'),
    db_name:        job?.db_name || '',
    db_host:        job?.db_host || '',
    db_port:        job?.db_port || '',
    db_user:        job?.db_user || '',
    db_password:    '',                     // nunca pre-populate; backend no lo expone
    oracle_service: job?.oracle_service || '',
    restic_connections: job?.restic_connections ?? 5,
    restic_compression: job?.restic_compression || 'auto',
    restic_pack_size:   job?.restic_pack_size ?? 128,
    max_duration_minutes: job?.max_duration_minutes ?? '',
    local_repo_path:    job?.local_repo_path || '',
    local_repo_mode:    job?.local_repo_mode || 'mirror',
    restic_excludes:     job ? (job.restic_excludes || []).join('\n') : DEFAULT_FILE_EXCLUDES.join('\n'),
    read_data_subset_pct: job?.read_data_subset_pct ?? 5,
    initial_load_state: job?.initial_load_state || 'off',
    // #192 D3, 2.c.2 — GiB, vacío = usar el default. job.initial_load_tranche_bytes
    // llega en bytes (list_jobs, 1.b); se muestra convertido a GiB.
    initial_load_tranche_gib: job?.initial_load_tranche_bytes
      ? String(Math.round(job.initial_load_tranche_bytes / GIB))
      : '',
    enabled:        job?.enabled !== false,
    m365_tenant_id:     job?.m365_tenant_id || '',
    m365_client_id:     job?.m365_client_id || '',
    m365_client_secret: '',               // nunca pre-populate secrets
    gworkspace_service_account_json: '', // nunca pre-populate
    gworkspace_delegated_user: job?.gworkspace_delegated_user || '',
    cloud_scope: job?.cloud_scope || 'mail,drive,calendar',
    cloud_users: (job?.cloud_users || []).join('\n'),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [cloudTesting,    setCloudTesting]    = useState(false);
  const [cloudTestResult, setCloudTestResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // #192 D3, 2.c.3 — "Entendido": el job ya se guardó, esto sólo cierra y
  // dispara el mismo refresco que un guardado sin advertencias.
  const acknowledgeWarnings = () => { onSaved(); onClose(); };

  // Mismo criterio que PUT/DELETE /jobs/{id}/anomaly-threshold (require_permission('can_manage_jobs')):
  // admin, o operador/reseller con can_manage_jobs. El rol client nunca (el backend le da 403).
  const { isAdmin, isOperator, isReseller, perms, resellerPerms } = useAuth() || {};
  const canManageThreshold = !!(isAdmin || (isOperator && perms?.can_manage_jobs) || (isReseller && resellerPerms?.can_manage_jobs));
  const thrLocked = isNew || !canManageThreshold;
  const defaultThreshold = { size_drop_pct: 70, data_added_spike_x: 5, min_history_runs: 5, action_notify: true, action_disable_job: false };
  const [anomalySource, setAnomalySource]       = useState('global');
  const [anomalyThreshold, setAnomalyThreshold] = useState(defaultThreshold);
  const [anomalySaving, setAnomalySaving]       = useState(false);
  const [anomalyMsg, setAnomalyMsg]             = useState('');

  const loadAnomalyThreshold = useCallback(async () => {
    try {
      if (isNew) {
        const global = await api.getGlobalAnomalyThreshold();
        setAnomalyThreshold(global || defaultThreshold);
        setAnomalySource('global');
      } else if (job?.id) {
        const res = await api.getJobAnomalyThreshold(job.id);
        setAnomalySource(res.source);
        setAnomalyThreshold(res.threshold || defaultThreshold);
      }
    } catch {}
  }, [job?.id, isNew]); // eslint-disable-line

  useEffect(() => { loadAnomalyThreshold(); }, [loadAnomalyThreshold]);

  const saveAnomalyThreshold = async () => {
    setAnomalySaving(true);
    setAnomalyMsg('');
    try {
      await api.upsertJobAnomalyThreshold(job.id, anomalyThreshold);
      setAnomalySource('custom');
      setAnomalyMsg('Guardado ✓');
    } catch { setAnomalyMsg('Error al guardar'); }
    finally { setAnomalySaving(false); }
  };

  const resetAnomalyToGlobal = async () => {
    setAnomalySaving(true);
    setAnomalyMsg('');
    try {
      await api.deleteJobAnomalyThreshold(job.id);
      await loadAnomalyThreshold();
      setAnomalyMsg('Reseteado a defaults globales ✓');
    } catch { setAnomalyMsg('Error al resetear'); }
    finally { setAnomalySaving(false); }
  };

  const setAnomaly = (k, v) => setAnomalyThreshold(t => ({ ...t, [k]: v }));

  useEffect(() => {
    setCloudTestResult(null);
    if (isNew && form.job_type === 'integrity_check') {
      set('schedule', '0 3 * * 0');
    }
  }, [form.job_type]); // eslint-disable-line

  const isDb             = ['pgsql', 'mssql', 'mysql', 'oracle'].includes(form.job_type);
  const isFilesJob       = form.job_type === 'files';
  const isIntegrityCheck = form.job_type === 'integrity_check';

  const submit = async () => {
    if (!form.name.trim())   { setError('El nombre es obligatorio'); return; }
    if (!form.server_id)     { setError('Seleccioná un servidor'); return; }
    if (isFilesJob && !form.paths.trim()) { setError('Ingresá al menos un path'); return; }
    if (form.job_type === 'oracle' && !form.oracle_service.trim()) {
      setError('Oracle requiere un Servicio / SID'); return;
    }
    // La carga inicial en curso pierde su avance al desactivarse (el próximo
    // backup sería completo, no incremental) — confirmación explícita, no
    // sólo el click del checkbox.
    let initialLoadConfirmReset = false;
    if (job?.initial_load_state === 'in_progress' && form.initial_load_state === 'off') {
      const ok = window.confirm(
        'La carga inicial está en curso. Desactivarla ahora descarta el avance ' +
        '(el próximo backup sería completo, no incremental). ¿Confirmás?'
      );
      if (!ok) return;
      initialLoadConfirmReset = true;
    }
    setLoading(true);
    setError('');
    try {
      const isM365      = form.job_type === 'm365';
      const isGWorkspace = form.job_type === 'gworkspace';
      const payload = {
        server_id:          form.server_id,
        name:               form.name.trim(),
        job_type:           form.job_type,
        schedule:           form.schedule || null,
        retention_days:     parseInt(form.retention_days) || 90,
        enabled:            form.enabled,
        paths:              isFilesJob ? form.paths.split('\n').map(p => p.trim()).filter(Boolean) : [],
        db_name:            isDb ? (form.db_name.trim() || null) : null,
        db_host:            isDb ? (form.db_host.trim() || null) : null,
        db_port:            isDb && form.db_port ? parseInt(form.db_port) : null,
        db_user:            isDb ? (form.db_user.trim() || null) : null,
        db_password:        isDb && form.db_password ? form.db_password : null,
        oracle_service:     form.job_type === 'oracle' ? (form.oracle_service.trim() || null) : null,
        restic_connections: parseInt(form.restic_connections) || 5,
        restic_compression: form.restic_compression || 'auto',
        restic_pack_size:   parseInt(form.restic_pack_size) || 128,
        // #187 — parseInt(...) || null convertía un "0" tipeado en null antes
        // de que llegara al backend, tapando el rechazo con mensaje claro que
        // pide la validación de ahí. Sólo NaN (campo vacío o no numérico) es null.
        max_duration_minutes: (() => {
          const n = parseInt(form.max_duration_minutes, 10);
          return Number.isNaN(n) ? null : n;
        })(),
        local_repo_path:    form.local_repo_path.trim() || null,
        local_repo_mode:    form.local_repo_mode || 'mirror',
        restic_excludes:     form.restic_excludes.split('\n').map(p => p.trim()).filter(Boolean),
        read_data_subset_pct: isIntegrityCheck ? (parseInt(form.read_data_subset_pct) || 5) : undefined,
        initial_load_state:         isFilesJob ? form.initial_load_state : 'off',
        initial_load_confirm_reset: initialLoadConfirmReset,
        initial_load_tranche_bytes: (() => {
          if (!isFilesJob || form.initial_load_tranche_gib === '') return null;
          const n = parseInt(form.initial_load_tranche_gib, 10);
          return Number.isNaN(n) ? null : n * GIB;
        })(),
        ...(isM365 && {
          m365_tenant_id:     form.m365_tenant_id.trim() || null,
          m365_client_id:     form.m365_client_id.trim() || null,
          m365_client_secret: form.m365_client_secret || null,
          cloud_scope:        form.cloud_scope || 'mail,drive,calendar',
          cloud_users:        (form.cloud_users || '').split('\n').map(e => e.trim().toLowerCase()).filter(Boolean),
        }),
        ...(isGWorkspace && {
          gworkspace_service_account_json: form.gworkspace_service_account_json || null,
          gworkspace_delegated_user:       form.gworkspace_delegated_user.trim() || null,
          cloud_scope:                     form.cloud_scope || 'mail,drive',
          cloud_users:                     (form.cloud_users || '').split('\n').map(e => e.trim().toLowerCase()).filter(Boolean),
        }),
      };
      const result = isNew ? await api.createJob(payload) : await api.updateJob(job.id, payload);
      // #192 D3, 2.c.3 — el job ya quedó guardado (createJob/updateJob ya
      // commitearon): si hay advertencias, el modal NO vuelve a guardar,
      // queda abierto mostrándolas, y recién el click en "Entendido" refresca
      // y cierra (ver acknowledgeWarnings). Sin advertencias, como antes.
      if (result?.warnings?.length) {
        setSaveWarnings(result.warnings);
      } else {
        onSaved();
        onClose();
      }
    } catch (e) { setError(e.message || 'Error al crear el job'); }
    finally { setLoading(false); }
  };

  const portPlaceholder = form.job_type === 'pgsql' ? '5432' : form.job_type === 'mssql' ? '1433' : form.job_type === 'mysql' ? '3306' : form.job_type === 'oracle' ? '1521' : '';
  const userPlaceholder = form.job_type === 'pgsql' ? 'postgres' : form.job_type === 'mssql' ? 'sa' : form.job_type === 'mysql' ? 'root' : form.job_type === 'oracle' ? 'system' : 'usuario';
  const dbNote = {
    pgsql:  '→ pg_dump / pg_dumpall. Usuario necesita CONNECT en la BD.',
    mssql:  '→ sqlcmd + BACKUP DATABASE. Usuario necesita rol db_backupoperator.',
    mysql:  '→ mysqldump --single-transaction. Usuario necesita SELECT, LOCK TABLES, SHOW VIEW, EVENT, TRIGGER.',
    oracle: '→ expdp (Data Pump). Requiere Oracle Client en el servidor. Usuario necesita EXP_FULL_DATABASE o DBA.',
  }[form.job_type] || '';

  const handleTestCloud = async () => {
    setCloudTesting(true);
    setCloudTestResult(null);
    try {
      const isM365 = form.job_type === 'm365';
      const payload = {
        provider: form.job_type,
        ...(job?.id ? { job_id: job.id } : {}),
        ...(isM365 ? {
          m365_tenant_id:     form.m365_tenant_id,
          m365_client_id:     form.m365_client_id,
          m365_client_secret: form.m365_client_secret || undefined,
          cloud_scope:        form.cloud_scope,
        } : {
          gworkspace_service_account_json: form.gworkspace_service_account_json || undefined,
          gworkspace_delegated_user:       form.gworkspace_delegated_user,
          cloud_scope:                     form.cloud_scope,
        }),
      };
      const res = await api.testCloudConfig(payload);
      setCloudTestResult(res);
    } catch (e) {
      setCloudTestResult({ ok: false, detail: e.message || 'Error al contactar el servidor.' });
    } finally {
      setCloudTesting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 16, boxSizing: 'border-box' }}>
      {/* #192 D3 (corrección) — el límite de altura va acá, en el modal
          entero (flex column + maxHeight), no en el cuerpo: así encabezado y
          pie quedan siempre visibles y lo único que se achica/scrollea es el
          cuerpo. Mismo patrón que el resto de los modales del frontend
          (EditClientModal, ServersTab, SnapshotExplorer, etc.) — JobModal
          era el único que ponía el maxHeight en el cuerpo con el pie afuera,
          sin límite en el contenedor: en una pantalla donde header+80vh+pie
          no entraban, el pie (y la advertencia que vive ahí) quedaba fuera
          del área visible sin nada que lo scrollee. El ancho pasa de un
          número fijo a 100% con un tope, para no desbordar en mobile. */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>{readOnly ? 'VER JOB' : isNew ? 'NUEVO JOB' : 'EDITAR JOB'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          {readOnly && (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>
              Los jobs de esta cuenta los gestiona tu proveedor: acá podés verlos, no modificarlos.
            </div>
          )}
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, pointerEvents: readOnly ? 'none' : undefined }}>

          {/* Nombre + Servidor */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Nombre</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Backup diario"
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Servidor</label>
              <select value={form.server_id} onChange={e => set('server_id', e.target.value)}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
                {servers.map(s => <option key={s.id} value={s.id}>{s.hostname}</option>)}
              </select>
            </div>
          </div>

          {/* Tipo */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Tipo</label>
            <select value={form.job_type} onChange={e => set('job_type', e.target.value)}
              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              <option value="files">Archivos</option>
              <option value="pgsql">PostgreSQL</option>
              <option value="mssql">SQL Server</option>
              <option value="mysql">MySQL / MariaDB</option>
              <option value="oracle">Oracle DB</option>
              <option value="m365">Microsoft 365</option>
              <option value="gworkspace">Google Workspace</option>
              <option value="integrity_check">Verificación de integridad del repositorio</option>
            </select>
          </div>

          {/* Programación */}
          <div style={{ marginBottom: 0 }}>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Programación</label>
            <ScheduleBuilder key={defaultHour} value={form.schedule} onChange={v => set('schedule', v)} />
          </div>

          {/* Paths */}
          {form.job_type === 'files' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Paths (uno por línea)</label>
              <textarea value={form.paths} onChange={e => set('paths', e.target.value)} rows={3}
                placeholder={"/var/www\n/home/usuario/documentos\nC:\\Datos"}
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', resize: 'vertical' }} />
            </div>
          )}

          {/* DB credentials */}
          {isDb && (
            <div style={{ marginBottom: 16, border: '1px solid var(--border)', padding: 14, background: 'rgba(0,212,255,0.03)' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 12, fontFamily: 'var(--mono)' }}>
                CONEXIÓN A BASE DE DATOS
              </div>

              {/* Host + Puerto */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={dbLbl}>Host</label>
                  <input value={form.db_host} onChange={e => set('db_host', e.target.value)} placeholder="localhost" style={dbInp} />
                </div>
                <div>
                  <label style={dbLbl}>Puerto</label>
                  <input type="number" value={form.db_port} onChange={e => set('db_port', e.target.value)}
                    placeholder={portPlaceholder} style={dbInp} />
                </div>
              </div>

              {/* Usuario + Contraseña */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={dbLbl}>Usuario</label>
                  <input value={form.db_user} onChange={e => set('db_user', e.target.value)}
                    placeholder={userPlaceholder} style={dbInp} />
                </div>
                <div>
                  <label style={dbLbl}>
                    Contraseña{' '}
                    {job?.has_db_password && !form.db_password &&
                      <span style={{ color: 'var(--accent)', fontSize: 9 }}>✓ configurada</span>}
                  </label>
                  <input type="password" value={form.db_password} onChange={e => set('db_password', e.target.value)}
                    placeholder={job?.has_db_password ? '(sin cambios)' : 'contraseña'}
                    style={dbInp} />
                </div>
              </div>

              {/* Servicio Oracle — solo visible cuando job_type === 'oracle' */}
              {form.job_type === 'oracle' && (
                <div style={{ marginBottom: 10 }}>
                  <label style={dbLbl}>
                    Servicio / SID <span style={{ color: 'var(--red)', fontSize: 9 }}>* requerido</span>
                  </label>
                  <input value={form.oracle_service} onChange={e => set('oracle_service', e.target.value)}
                    placeholder="ORCL  o  mi_servicio.empresa.com"
                    style={dbInp} />
                </div>
              )}

              {/* Base de datos / Schema */}
              <div>
                <label style={dbLbl}>
                  {form.job_type === 'oracle' ? 'Schema (vacío = FULL)' : 'Base de datos (vacío = todas)'}
                </label>
                <input value={form.db_name} onChange={e => set('db_name', e.target.value)}
                  placeholder={
                    form.job_type === 'pgsql'  ? 'mi_base  o  vacío para pg_dumpall' :
                    form.job_type === 'mysql'  ? 'mi_base  o  vacío para --all-databases' :
                    form.job_type === 'oracle' ? 'MI_SCHEMA  o  vacío para FULL=Y' :
                    'NombreDB'
                  }
                  style={dbInp} />
              </div>

              {/* Nota informativa por motor */}
              {dbNote && (
                <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', lineHeight: 1.6 }}>
                  {dbNote}
                </div>
              )}
            </div>
          )}

          {/* M365 credentials */}
          {form.job_type === 'm365' && (
            <div style={{ marginBottom: 16, padding: 16, border: '1px solid var(--border)', background: 'var(--bg)' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--accent)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 12 }}>
                Microsoft 365 — Azure AD App Registration
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Tenant ID</label>
                <input value={form.m365_tenant_id} onChange={e => set('m365_tenant_id', e.target.value)}
                  style={dbInp} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Client ID (Application ID)</label>
                <input value={form.m365_client_id} onChange={e => set('m365_client_id', e.target.value)}
                  style={dbInp} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>
                  Client Secret{' '}
                  {job?.has_m365_client_secret && !form.m365_client_secret &&
                    <span style={{ color: 'var(--accent)', fontSize: 9 }}>✓ configurado</span>}
                </label>
                <input type="password" value={form.m365_client_secret}
                  onChange={e => set('m365_client_secret', e.target.value)}
                  style={dbInp}
                  placeholder={job?.has_m365_client_secret ? '(sin cambios)' : '••••••••••••••••••••••••'} />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                  Requerido: Mail.Read, Files.Read.All, Calendars.Read (Application permissions)
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Scopes a respaldar</label>
                {['mail','drive','calendar','teams'].map(s => (
                  <label key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 16, fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={(form.cloud_scope || '').split(',').includes(s)}
                      onChange={e => {
                        const current = (form.cloud_scope || '').split(',').filter(Boolean);
                        const updated = e.target.checked
                          ? [...new Set([...current, s])]
                          : current.filter(x => x !== s);
                        set('cloud_scope', updated.join(','));
                      }}
                    />
                    {s}
                  </label>
                ))}
              </div>
              {/* Scope info panel M365 */}
              {(() => {
                const selected = (form.cloud_scope || '').split(',').filter(Boolean);
                const scopeMap = { mail: 'Mail.Read', drive: 'Files.Read.All', calendar: 'Calendars.Read', teams: 'Chat.Read.All' };
                const required = selected.filter(s => scopeMap[s]).map(s => scopeMap[s]);
                if (!required.length) return null;
                return (
                  <div style={{ marginTop: 12, marginBottom: 12, padding: 12, background: 'rgba(255,180,0,0.06)', border: '1px solid rgba(255,180,0,0.28)', borderRadius: 3 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#ffb400', marginBottom: 6, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      Application permissions requeridos en Azure
                    </div>
                    {required.map(p => (
                      <div key={p} style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text)', marginBottom: 2 }}>• {p}</div>
                    ))}
                    <div style={{ marginTop: 8, fontSize: 11, color: '#ffb400' }}>
                      ⚠ Después de agregar los permisos en la App Registration, hay que hacer "Grant admin consent for [tenant]". Sin ese paso el backup falla con error 403.
                    </div>
                  </div>
                );
              })()}
              <div style={{ marginTop: 16 }}>
                <label style={dbLbl}>Usuarios a respaldar</label>
                <textarea
                  value={form.cloud_users}
                  onChange={e => set('cloud_users', e.target.value)}
                  style={{ ...dbInp, height: 80, resize: 'vertical' }}
                  placeholder={'usuario1@empresa.com\nusuario2@empresa.com'}
                />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                  Un email por línea. Vacío = todos los usuarios del tenant.
                </div>
              </div>
              {/* Probar configuración M365 */}
              {(() => {
                const disabled = !form.m365_tenant_id || !form.m365_client_id ||
                  (!form.m365_client_secret && !job?.has_m365_client_secret);
                return (
                  <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" onClick={handleTestCloud}
                      disabled={cloudTesting || disabled}
                      style={{ padding: '7px 16px', fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: 1,
                        textTransform: 'uppercase', cursor: disabled || cloudTesting ? 'not-allowed' : 'pointer',
                        background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)',
                        opacity: (cloudTesting || disabled) ? 0.45 : 1 }}>
                      {cloudTesting ? 'PROBANDO...' : 'PROBAR CONFIGURACIÓN'}
                    </button>
                    {cloudTestResult && (
                      <div style={{ fontSize: 12, color: cloudTestResult.ok ? '#4caf50' : '#f44336', flex: 1 }}>
                        {cloudTestResult.ok ? '✓ ' : '✗ '}{cloudTestResult.detail}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* GWorkspace credentials */}
          {form.job_type === 'gworkspace' && (
            <div style={{ marginBottom: 16, padding: 16, border: '1px solid var(--border)', background: 'var(--bg)' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--accent)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 12 }}>
                Google Workspace — Service Account
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>
                  Service Account JSON{' '}
                  {job?.has_gworkspace_service_account_json && !form.gworkspace_service_account_json &&
                    <span style={{ color: 'var(--accent)', fontSize: 9 }}>✓ configurada</span>}
                </label>
                <textarea
                  value={form.gworkspace_service_account_json}
                  onChange={e => set('gworkspace_service_account_json', e.target.value)}
                  style={{ ...dbInp, height: 120, resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 11 }}
                  placeholder={job?.has_gworkspace_service_account_json
                    ? '(sin cambios — pegá nuevo JSON solo si querés reemplazar)'
                    : '{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'}
                />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                  Pegar el contenido completo del archivo .json descargado de Google Cloud Console.
                  Requiere delegación de dominio activada.
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Usuario delegado (admin del dominio)</label>
                <input value={form.gworkspace_delegated_user}
                  onChange={e => set('gworkspace_delegated_user', e.target.value)}
                  style={dbInp} placeholder="admin@tudominio.com" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Scopes a respaldar</label>
                {['mail','drive','calendar'].map(s => (
                  <label key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 16, fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={(form.cloud_scope || '').split(',').includes(s)}
                      onChange={e => {
                        const current = (form.cloud_scope || '').split(',').filter(Boolean);
                        const updated = e.target.checked
                          ? [...new Set([...current, s])]
                          : current.filter(x => x !== s);
                        set('cloud_scope', updated.join(','));
                      }}
                    />
                    {s}
                  </label>
                ))}
              </div>
              {/* Scope info panel GWorkspace */}
              {(() => {
                const selected = (form.cloud_scope || '').split(',').filter(Boolean);
                const scopeMap = {
                  mail:     'https://www.googleapis.com/auth/gmail.readonly',
                  drive:    'https://www.googleapis.com/auth/drive.readonly',
                  calendar: 'https://www.googleapis.com/auth/calendar.readonly',
                };
                const required = selected.filter(s => scopeMap[s]).map(s => scopeMap[s]);
                if (!required.length) return null;
                return (
                  <div style={{ marginTop: 12, marginBottom: 12, padding: 12, background: 'rgba(255,180,0,0.06)', border: '1px solid rgba(255,180,0,0.28)', borderRadius: 3 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#ffb400', marginBottom: 6, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: 1 }}>
                      OAuth scopes requeridos (Domain-wide Delegation)
                    </div>
                    {required.map(p => (
                      <div key={p} style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text)', marginBottom: 2, wordBreak: 'break-all' }}>• {p}</div>
                    ))}
                    <div style={{ marginTop: 8, fontSize: 11, color: '#ffb400' }}>
                      ⚠ Estos scopes deben estar autorizados en Admin Console → Security → API Controls → Domain-wide Delegation, con el Client ID de la Service Account. Sin eso el backup falla con error 403.
                    </div>
                  </div>
                );
              })()}
              <div style={{ marginTop: 16 }}>
                <label style={dbLbl}>Usuarios a respaldar</label>
                <textarea
                  value={form.cloud_users}
                  onChange={e => set('cloud_users', e.target.value)}
                  style={{ ...dbInp, height: 80, resize: 'vertical' }}
                  placeholder={'usuario1@dominio.com\nusuario2@dominio.com'}
                />
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                  Un email por línea. Vacío = todos los usuarios del dominio.
                </div>
              </div>
              {/* Probar configuración GWorkspace */}
              {(() => {
                const disabled = (!form.gworkspace_service_account_json && !job?.has_gworkspace_service_account_json) ||
                  !form.gworkspace_delegated_user;
                return (
                  <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" onClick={handleTestCloud}
                      disabled={cloudTesting || disabled}
                      style={{ padding: '7px 16px', fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: 1,
                        textTransform: 'uppercase', cursor: disabled || cloudTesting ? 'not-allowed' : 'pointer',
                        background: 'transparent', border: '1px solid var(--accent)', color: 'var(--accent)',
                        opacity: (cloudTesting || disabled) ? 0.45 : 1 }}>
                      {cloudTesting ? 'PROBANDO...' : 'PROBAR CONFIGURACIÓN'}
                    </button>
                    {cloudTestResult && (
                      <div style={{ fontSize: 12, color: cloudTestResult.ok ? '#4caf50' : '#f44336', flex: 1 }}>
                        {cloudTestResult.ok ? '✓ ' : '✗ '}{cloudTestResult.detail}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Opciones Avanzadas Restic — no aplica a integrity_check */}
          {!isIntegrityCheck && <div style={{ marginBottom: 16, border: '1px solid var(--border)', padding: 14, background: 'rgba(0,212,255,0.02)' }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12, fontFamily: 'var(--mono)' }}>
              OPCIONES AVANZADAS RESTIC
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={dbLbl}>Conexiones (1-50)</label>
                <input type="number" min={1} max={50} value={form.restic_connections}
                  onChange={e => set('restic_connections', parseInt(e.target.value) || 5)}
                  style={dbInp} />
              </div>
              <div>
                <label style={dbLbl}>Compresión</label>
                <select value={form.restic_compression} onChange={e => set('restic_compression', e.target.value)}
                  style={{ ...dbInp, cursor: 'pointer' }}>
                  <option value="auto">auto</option>
                  <option value="max">max</option>
                  <option value="off">off</option>
                </select>
              </div>
              <div>
                <label style={dbLbl}>Pack size (MB)</label>
                <input type="number" min={1} value={form.restic_pack_size}
                  onChange={e => set('restic_pack_size', parseInt(e.target.value) || 128)}
                  style={dbInp} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={dbLbl}>Ventana máxima por corrida (minutos, vacío = sin límite)</label>
              <input type="number" min={1} value={form.max_duration_minutes}
                onChange={e => set('max_duration_minutes', e.target.value)}
                placeholder="vacío = sin límite / usa el default global"
                style={dbInp} />
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                Ej: 480 = 8 horas, 90 = 1 hora y media
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={dbLbl}>Repo local (vacío = almacenamiento directo)</label>
              <input value={form.local_repo_path} onChange={e => set('local_repo_path', e.target.value)}
                placeholder="vacío = almacenamiento directo"
                style={dbInp} />
            </div>
            {form.local_repo_path && (
              <div style={{ marginTop: 10 }}>
                <label style={dbLbl}>Modo repo local</label>
                <select value={form.local_repo_mode || 'mirror'} onChange={e => set('local_repo_mode', e.target.value)} style={dbInp}>
                  <option value="mirror">Espejo permanente — backup local + copia a Wasabi en cada run</option>
                  <option value="bootstrap">Bootstrap — primer run local, luego directo a Wasabi y borra repo local</option>
                </select>
                {form.local_repo_mode === 'bootstrap' && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                    El primer backup usa el repo local para mayor velocidad. Una vez copiado y verificado en Wasabi, el repo local se elimina automáticamente. Los backups siguientes van directo a Wasabi.
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <label style={dbLbl}>Exclusiones (uno por línea)</label>
              <textarea value={form.restic_excludes} onChange={e => set('restic_excludes', e.target.value)}
                rows={4}
                placeholder={"*.tmp\npagefile.sys\nhiberfil.sys\n$Recycle.Bin\nSystem Volume Information"}
                style={{ ...dbInp, resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 12 }} />
            </div>
            {isFilesJob && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={form.initial_load_state !== 'off'}
                      onChange={e => set('initial_load_state', e.target.checked ? 'pending' : 'off')} />
                    Carga inicial en tramos (datasets enormes)
                  </label>
                  {form.initial_load_state !== 'off' && (
                    <div>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                        Tamaño de tramo (GiB):
                        <input type="number" min={1} step={1}
                          value={form.initial_load_tranche_gib}
                          onChange={e => set('initial_load_tranche_gib', e.target.value)}
                          placeholder="por defecto"
                          style={{ width: 130, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 8px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none' }} />
                      </label>
                      {gibToGbHelpText(form.initial_load_tranche_gib) && (
                        <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                          {gibToGbHelpText(form.initial_load_tranche_gib)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                  Divide la primera carga en corridas sucesivas dentro de la ventana máxima
                  configurada, en vez de un solo backup que nunca termina.
                  {job && initialLoadStatusText(job) && (
                    <> Estado actual: <strong>{initialLoadStatusText(job)}</strong>.</>
                  )}
                </div>
              </div>
            )}
          </div>}

          {/* integrity_check: porcentaje a verificar */}
          {isIntegrityCheck && (
            <div style={{ marginBottom: 16, border: '1px solid var(--border)', padding: 14, background: 'rgba(59,130,246,0.03)' }}>
              <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#3b82f6', marginBottom: 10, fontFamily: 'var(--mono)' }}>
                OPCIONES DE VERIFICACIÓN
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={dbLbl}>Porcentaje del repositorio a verificar (%)</label>
                <input type="number" min={1} max={100} value={form.read_data_subset_pct}
                  onChange={e => set('read_data_subset_pct', parseInt(e.target.value) || 5)}
                  style={{ ...dbInp, width: 120 }} />
              </div>
              <div style={{ border: '1px solid var(--border)', borderLeft: '3px solid #3b82f6', padding: '10px 12px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', background: 'rgba(59,130,246,0.05)', lineHeight: 1.6 }}>
                ℹ Lee N% de los archivos de datos al azar. Recomendado: 5% semanal cubre ~93% del repo en 1 año. 100% trimestral hace verificación completa.<br/>
                No genera snapshots ni toca el filesystem del cliente.
                Wasabi no cobra egress dentro de su política de uso razonable (egress mensual ≤ storage activo).
              </div>
            </div>
          )}

          {/* Detección de anomalías */}
          <div style={{ marginBottom: 16, border: '1px solid var(--border)', padding: 14, background: 'rgba(245,158,11,0.03)' }}>
            <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--yellow)', marginBottom: 4, fontFamily: 'var(--mono)' }}>
              DETECCIÓN DE ANOMALÍAS
              {!isNew && anomalySource === 'global' && <span style={{ color: 'var(--muted)', marginLeft: 8 }}>(usando defaults globales)</span>}
              {!isNew && anomalySource === 'custom' && <span style={{ color: 'var(--accent)', marginLeft: 8 }}>(config personalizada)</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
              Compara el run actual con el historial. Detecta señales de ransomware (cifrado masivo, borrado de archivos).
            </div>
            {!isNew && !canManageThreshold && (
              <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                Sólo lectura: tu cuenta no puede modificar los umbrales de detección.
              </div>
            )}
            {isNew && (
              <div style={{ border: '1px solid var(--border)', borderLeft: '3px solid #3b82f6', padding: '10px 12px', marginBottom: 12, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', background: 'rgba(59,130,246,0.05)' }}>
                ℹ Este job va a usar los umbrales globales de detección. Después de crearlo, podés personalizarlos editándolo.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={dbLbl}>Caída de tamaño (%)</label>
                <input type="number" min={0} max={100} value={anomalyThreshold.size_drop_pct}
                  onChange={thrLocked ? undefined : e => setAnomaly('size_drop_pct', parseFloat(e.target.value) || 70)}
                  disabled={thrLocked} style={{ ...dbInp, opacity: thrLocked ? 0.5 : 1 }} />
              </div>
              <div>
                <label style={dbLbl}>Pico data_added (x)</label>
                <input type="number" min={1} step={0.5} value={anomalyThreshold.data_added_spike_x}
                  onChange={thrLocked ? undefined : e => setAnomaly('data_added_spike_x', parseFloat(e.target.value) || 5)}
                  disabled={thrLocked} style={{ ...dbInp, opacity: thrLocked ? 0.5 : 1 }} />
              </div>
              <div>
                <label style={dbLbl}>Mínimo runs históricos</label>
                <input type="number" min={2} max={50} value={anomalyThreshold.min_history_runs}
                  onChange={thrLocked ? undefined : e => setAnomaly('min_history_runs', parseInt(e.target.value) || 5)}
                  disabled={thrLocked} style={{ ...dbInp, opacity: thrLocked ? 0.5 : 1 }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: thrLocked ? 0 : 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: thrLocked ? 'default' : 'pointer', color: thrLocked ? 'var(--muted)' : 'var(--text)' }}>
                <input type="checkbox" checked={anomalyThreshold.action_notify}
                  onChange={thrLocked ? undefined : e => setAnomaly('action_notify', e.target.checked)} disabled={thrLocked} />
                Notificar (Telegram + email)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: thrLocked ? 'default' : 'pointer', color: thrLocked ? 'var(--muted)' : 'var(--text)' }}>
                <input type="checkbox" checked={anomalyThreshold.action_disable_job}
                  onChange={thrLocked ? undefined : e => setAnomaly('action_disable_job', e.target.checked)} disabled={thrLocked} />
                Pausar job si score ≥ 67%
              </label>
            </div>
            {!thrLocked && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                <button onClick={saveAnomalyThreshold} disabled={anomalySaving}
                  style={{ background: 'transparent', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '5px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
                  {anomalySaving ? 'GUARDANDO...' : 'GUARDAR UMBRALES'}
                </button>
                {anomalySource === 'custom' && (
                  <button onClick={resetAnomalyToGlobal} disabled={anomalySaving}
                    style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
                    RESET A GLOBALES
                  </button>
                )}
                {anomalyMsg && <span style={{ fontSize: 11, color: anomalyMsg.includes('Error') ? 'var(--red)' : 'var(--green)', fontFamily: 'var(--mono)' }}>{anomalyMsg}</span>}
              </div>
            )}
          </div>

          {/* Retención + Toggle habilitado */}
          <div style={{ display: 'grid', gridTemplateColumns: isIntegrityCheck ? '1fr' : '1fr 1fr', gap: 16 }}>
            {!isIntegrityCheck && (
              <div>
                <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>Retención (días)</label>
                <input type="number" value={form.retention_days} onChange={e => set('retention_days', e.target.value)}
                  style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none' }} />
                {parseInt(form.retention_days) < 90 && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--yellow)', fontFamily: 'var(--mono)' }}>
                    ⚠ Wasabi cobra un mínimo de 90 días por objeto. Configurar menos de 90 días puede generar cargos adicionales.
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: isIntegrityCheck ? 0 : 24 }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Habilitado</span>
              <div onClick={() => set('enabled', !form.enabled)} style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', background: form.enabled ? 'var(--accent)' : 'var(--border)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: 'white', left: form.enabled ? 18 : 2, transition: 'left 0.2s' }} />
              </div>
            </div>
          </div>
          </fieldset>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          {saveWarnings.length > 0 ? (
            <>
              {/* Alto máximo con scroll propio: una advertencia larga no se
                  come el modal entero en un celular — el pie sigue siendo
                  chico y el botón ENTENDIDO queda siempre a la vista. */}
              <div style={{ maxHeight: '30vh', overflowY: 'auto', marginBottom: 10 }}>
                {saveWarnings.map((w, i) => (
                  <div key={i} style={{ background: 'rgba(255,209,102,0.1)', border: '1px solid var(--yellow)', color: 'var(--yellow)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: i < saveWarnings.length - 1 ? 8 : 0 }}>
                    ⚠ {w}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={acknowledgeWarnings} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
                  ENTENDIDO
                </button>
              </div>
            </>
          ) : (
            <>
              {error && <div style={{ color: 'var(--red)', fontFamily: 'var(--mono)', fontSize: 12, marginBottom: 10 }}>✕ {error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>{readOnly ? 'CERRAR' : 'CANCELAR'}</button>
                {!readOnly && <button onClick={submit} disabled={loading} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>
                  {loading ? 'GUARDANDO...' : isNew ? 'CREAR →' : 'GUARDAR →'}
                </button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
